import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  acceptMyInterventionRequest,
  declineMyInterventionRequest,
  fetchMyInterventionRequests,
  type DeclineRequestInput,
  type MyInterventionRequest,
} from '../api/myInterventionRequestsApi';

const KEY = 'my-intervention-requests';

/**
 * The technician's own requests. `refetchOnMount` is on: the WebView keeps the page alive across
 * tab switches, and a stale list is exactly how he ends up answering a mission somebody else was
 * already given — the wrong this screen exists to prevent.
 *
 * `enabled` because the `/issues` segment reads the same key for its badge, and the four other
 * roles that share that screen have nothing here to ask for.
 */
export const useMyInterventionRequests = (enabled = true) =>
  useQuery<MyInterventionRequest[]>({
    queryKey: [KEY],
    queryFn: fetchMyInterventionRequests,
    refetchOnMount: true,
    enabled,
  });

/**
 * Written out twice rather than behind a generic wrapper, as `useUnavailabilities` already is: the
 * two differ by their payload, and a wrapper would be one more abstraction instead of one less
 * copy.
 */
export const useAcceptMyInterventionRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => acceptMyInterventionRequest(id),
    // The app default retries a mutation once. Nothing here is idempotent: a replayed POST whose
    // response was lost but whose write landed comes back 409 `ALREADY_ANSWERED`, and the screen
    // would report a failure for an answer that is recorded.
    retry: false,
    // `onSettled` and not `onSuccess`: a 409 is precisely when the list is stale — re-reading it
    // is what makes the action footer disappear instead of offering an answer twice.
    onSettled: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeclineMyInterventionRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: DeclineRequestInput & { id: number }) =>
      declineMyInterventionRequest(id, input),
    retry: false,
    onSettled: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
};
